/* Brand films and editorial dialogs only. Commerce remains native Dawn. */
(() => {
  if (!customElements.get('lc-brand-film')) {
    class LedclawBrandFilm extends HTMLElement {
      connectedCallback() {
        this.controller?.abort();
        this.controller = new AbortController();
        const signal = this.controller.signal;
        this.videos = [...this.querySelectorAll('video')];
        if (!this.videos.length) return;
        this.reduce = matchMedia('(prefers-reduced-motion: reduce)');
        this.mobile = matchMedia('(max-width: 749px)');
        this.visible = false;
        this.userPaused = new WeakSet();
        this.programmaticPauses = new WeakSet();
        this.videos.forEach(video => {
          video.muted = true;
          video.addEventListener('pause', () => {
            if (this.programmaticPauses.has(video)) this.programmaticPauses.delete(video);
            else this.userPaused.add(video);
          }, { signal });
          video.addEventListener('play', () => this.userPaused.delete(video), { signal });
        });
        this.observer = new IntersectionObserver(entries => {
          this.visible = entries[0].isIntersecting;
          this.sync();
        }, { threshold: 0.12 });
        this.observer.observe(this);
        this.reduce.addEventListener('change', () => this.sync(), { signal });
        this.mobile.addEventListener('change', () => this.sync(), { signal });
        document.addEventListener('visibilitychange', () => this.sync(), { signal });
      }
      pause(video) {
        if (!video.paused) {
          this.programmaticPauses.add(video);
          video.pause();
        }
      }
      sync() {
        this.videos.forEach(video => {
          const onScreen = video.getBoundingClientRect().width > 0;
          if (!onScreen || !this.visible || document.hidden) this.pause(video);
          else if (this.reduce.matches || navigator.connection?.saveData) this.pause(video);
          else if (this.dataset.autoplay === 'true' && !this.userPaused.has(video)) video.play().catch(() => {});
        });
      }
      disconnectedCallback() {
        this.controller?.abort();
        this.observer?.disconnect();
        this.videos?.forEach(video => this.pause(video));
      }
    }
    customElements.define('lc-brand-film', LedclawBrandFilm);
  }
  function openDialog(id, trigger) {
    const dialog = document.getElementById(id);
    if (!dialog || typeof dialog.showModal !== 'function') return false;
    dialog.lcTrigger = trigger;
    dialog.showModal();
    document.documentElement.style.overflow = 'hidden';
    if (!dialog.dataset.lcBound) {
      dialog.dataset.lcBound = 'true';
      dialog.addEventListener('close', () => {
        document.documentElement.style.overflow = '';
        dialog.lcTrigger?.focus();
      });
      dialog.addEventListener('click', event => {
        if (event.target !== dialog) return;
        const r = dialog.getBoundingClientRect();
        if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) dialog.close();
      });
    }
    return true;
  }
  document.addEventListener('click', event => {
    const trigger = event.target.closest('[data-dialog-open]');
    if (trigger && !event.metaKey && !event.ctrlKey && !event.shiftKey && openDialog(trigger.dataset.dialogOpen, trigger)) event.preventDefault();
    const closer = event.target.closest('[data-dialog-close]');
    if (closer) closer.closest('dialog')?.close();
  });
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('dialog[open]').forEach(dialog => dialog.close());
  });
})();

/* v4.12.2: hero video collage. Tile k cycles clips k, k+slots, ...; one tile changes every few seconds; plays only on screen. */
(() => {
  if (customElements.get('lc-collage')) return;
  class LedclawCollage extends HTMLElement {
    connectedCallback() {
      this.clips = [...this.querySelectorAll('.lw-clip')];
      this.reduce = matchMedia('(prefers-reduced-motion: reduce)');
      this.mq = matchMedia('(max-width: 749px)');
      this.onMq = () => this.layout();
      this.mq.addEventListener?.('change', this.onMq);
      this.onVis = () => this.sync();
      document.addEventListener('visibilitychange', this.onVis);
      this.visible = false;
      this.io = new IntersectionObserver(e => { this.visible = e[0].isIntersecting; this.sync(); }, { threshold: 0.1 });
      this.io.observe(this);
      this.layout();
    }
    layout() {
      const slots = Math.max(1, Math.min(this.clips.length, this.mq.matches ? 2 : 3));
      this.slots = slots; this.turn = 0;
      this.style.setProperty('--slots', slots);
      this.lanes = Array.from({ length: slots }, () => []);
      this.clips.forEach((c, i) => { const k = i % slots; c.style.setProperty('--slot', k + 1); this.lanes[k].push(c); c.classList.toggle('is-on', i < slots); });
      this.pos = this.lanes.map(() => 0);
      this.sync();
    }
    tick() {
      const lanes = this.lanes.map((l, k) => k).filter(k => this.lanes[k].length > 1);
      if (!lanes.length) return;
      const k = lanes[this.turn++ % lanes.length], lane = this.lanes[k];
      const was = lane[this.pos[k]];
      this.pos[k] = (this.pos[k] + 1) % lane.length;
      const next = lane[this.pos[k]], v = next.querySelector('video');
      if (v) { v.preload = 'auto'; v.currentTime = 0; v.play().catch(() => {}); }
      next.classList.add('is-on'); was.classList.remove('is-on');
      setTimeout(() => { if (!was.classList.contains('is-on')) was.querySelector('video')?.pause(); }, 1000);
    }
    sync() {
      const run = this.visible && !document.hidden && !this.reduce.matches && !navigator.connection?.saveData;
      this.clips.forEach(c => {
        const v = c.querySelector('video'); if (!v) return;
        v.muted = true; v.playsInline = true;
        if (run && c.classList.contains('is-on')) { v.preload = 'auto'; v.play().catch(() => {}); } else v.pause();
      });
      clearInterval(this.timer);
      if (run && this.clips.length > this.slots) this.timer = setInterval(() => this.tick(), Math.max(3, Number(this.dataset.seconds) || 6) * 1000);
    }
    disconnectedCallback() {
      clearInterval(this.timer); this.io?.disconnect();
      this.mq?.removeEventListener?.('change', this.onMq);
      document.removeEventListener('visibilitychange', this.onVis);
    }
  }
  customElements.define('lc-collage', LedclawCollage);
})();
